/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, useEffect, useMemo, useState } from "react"
import { Drawer, Col, Row, Input, List, Checkbox, Button } from "@duxton-web/ui"
import { ChevronLeftUniversal, SearchUniversal } from "@duxton-web/icons"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { debounce } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { ModifierGroup } from "types/models/menu"
import { generateModifierGroupRule } from "utils/item_helper"
import { OverflowTip } from "components/common"
import LoadMore from "components/common/LoadMore"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import {
  useMenuV3ModifierGroupsInfinite,
  flattenInfiniteModifierGroups,
  useSearchMenusForMexQuery,
} from "queries"
import { LinkModifierGroupDrawerLoading } from "./LinkModifierGroupDrawerLoading"
import { listModifierGroup, drawerFooter } from "./styles"

interface props {
  visible: boolean
  onClose(): void
}

export const LinkModifierGroupDrawer: FC<props> = ({
  visible,
  onClose,
}: props) => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()

  const {
    menu: { modifierGroups },
    merchantID,
    merchantGroupID,
    drawer: { linkedModifierGroupsDrawerState },
  } = useSelector((state: AppState) => state.menu)

  const { linkedModifierGroups } = linkedModifierGroupsDrawerState ?? {}

  // Fetch modifier groups using RTK Query (only when feature flag is on)
  const {
    currentData: modifierGroupsData,
    isLoading: isLoadingModifierGroups,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error: modifierGroupsError,
  } = useMenuV3ModifierGroupsInfinite(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !merchantID || !enablePartialMenu,
    },
  )

  // RTK modifier groups for new implementation
  const modifierGroupsNew = useMemo(() => {
    const groups = flattenInfiniteModifierGroups(modifierGroupsData) || []
    return (groups as unknown) as ModifierGroup[]
  }, [modifierGroupsData])

  // Use RTK Query data when feature flag is on, otherwise use Redux data
  const allModifierGroups = enablePartialMenu
    ? modifierGroupsNew
    : modifierGroups

  const [searchKey, setSearchKey] = useState("")

  // Debounced search handler to prevent excessive API calls
  const debouncedSetSearchKey = debounce(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchKey(e?.target?.value),
    DEFAULT_DEBOUNCE_WAIT_TIME,
  )

  // Check if server-side search is active
  const isSearchActive = enablePartialMenu && !!searchKey

  // Server-side search for modifier groups (when partial menu is enabled)
  const {
    data: searchResults,
    isFetching: isSearchingModifierGroups,
  } = useSearchMenusForMexQuery(
    {
      merchantID,
      merchantGroupID,
      keyword: searchKey,
      entityType: "OPTION",
    },
    {
      skip: !isSearchActive,
    },
  )

  // Extract search results - filter to only include modifier groups where NAME matches
  // Exclude results where only modifier items match the search
  const searchedModifierGroups = useMemo(
    () =>
      searchResults?.modifierGroups
        ? ((searchResults.modifierGroups as unknown) as ModifierGroup[]).filter(
            mg =>
              mg.modifierGroupName
                ?.toLowerCase()
                .includes(searchKey.toLowerCase()),
          )
        : [],
    [searchResults, searchKey],
  )

  const [changeIsMade, setChangeIsMade] = useState(false)
  const [tmpLinkedModifierGroup, setTmpLinkedModifierGroup] = useState<
    ModifierGroup[]
  >([])

  useEffect(() => {
    if (linkedModifierGroups) {
      setTmpLinkedModifierGroup(linkedModifierGroups)
    }
  }, [linkedModifierGroups])

  const textMap = useTranslationsMap({
    modifierGroupPickUpTo: "menu.item.modifierGroup.pickUpTo",
    pickExactly: "menu.item.modifierGroup.pick",
    pickXtoY: "menu.item.modifierGroup.pickXtoY",
    drawerTitle: "menu.drawers.item.linkOptionGroupDrawerTitle",
    linkBtn: "menu.drawers.item.linkOptionGroupDrawerBtn",
    searchPlaceholder: "menu.drawers.item.linkOptionGroupDrawerPlaceholder",
  })

  const handleCheckModifierGroup = (modifierGroup: ModifierGroup) => {
    const mgExist = tmpLinkedModifierGroup?.find(
      mg => mg?.modifierGroupID === modifierGroup?.modifierGroupID,
    )
    if (!mgExist) {
      // check
      setTmpLinkedModifierGroup(prev => [...prev, modifierGroup])
    } else {
      // uncheck
      setTmpLinkedModifierGroup(prev => [
        ...prev?.filter(
          mg => mg?.modifierGroupID !== modifierGroup?.modifierGroupID,
        ),
      ])
    }

    setChangeIsMade(true)
  }

  const handleSubmitLinkMg = () => {
    dispatch(
      actionCreator.menu.updateLinkedModifierGroup(tmpLinkedModifierGroup),
    )
    onClose()
  }

  const filteredModifierGroups = useMemo(
    () =>
      isSearchActive
        ? searchedModifierGroups
        : allModifierGroups?.filter(mg =>
            mg?.modifierGroupName
              .toLocaleLowerCase()
              .includes(searchKey.toLocaleLowerCase()),
          ),
    [isSearchActive, searchedModifierGroups, allModifierGroups, searchKey],
  )

  const isLoadingModifierGroupsData =
    enablePartialMenu && (isLoadingModifierGroups || isSearchingModifierGroups)

  // Load more handler
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage && fetchNextPage) {
      fetchNextPage()
    }
  }

  // Render load more component (hidden when search is active)
  const renderLoadMore = () => {
    if (isSearchActive) return null

    if (enablePartialMenu && hasNextPage && !modifierGroupsError) {
      return (
        <LoadMore
          onLoadMore={handleLoadMore}
          component={<LinkModifierGroupDrawerLoading />}
        />
      )
    }
    return null
  }

  return (
    <Drawer
      title={textMap.drawerTitle}
      open={visible}
      onClose={onClose}
      width={450}
      destroyOnClose
      closeIcon={<ChevronLeftUniversal />}
    >
      <div
        css={css`
          padding: 23px;
        `}
      >
        <Row>
          <Col span={24}>
            <Input
              data-testid="search-modgroup"
              placeholder={textMap.searchPlaceholder}
              onChange={debouncedSetSearchKey}
              prefix={<SearchUniversal />}
            />
          </Col>
        </Row>
        <Row css={listModifierGroup}>
          <Col span={24}>
            <List
              itemLayout="horizontal"
              loading={isLoadingModifierGroupsData}
              loadMore={renderLoadMore()}
              locale={{
                emptyText: useTranslation(
                  "menu.item.modifierGroup.noDataAvailable",
                ),
              }}
              dataSource={filteredModifierGroups}
              renderItem={modifierGroup => (
                <List.Item
                  style={{ cursor: "pointer" }}
                  data-testid={`list-item-${modifierGroup.modifierGroupID}`}
                  onClick={() => handleCheckModifierGroup(modifierGroup)}
                >
                  <List.Item.Meta
                    data-testid={`list-item-${modifierGroup.modifierGroupID}-meta`}
                    css={css`
                      min-width: 0;
                      .dui-list-item-meta-content {
                        min-width: 0;
                      }
                    `}
                    avatar={
                      <Checkbox
                        checked={
                          !!tmpLinkedModifierGroup?.find(
                            mg =>
                              mg.modifierGroupID ===
                              modifierGroup?.modifierGroupID,
                          )
                        }
                      />
                    }
                    description={
                      <div>
                        <div className="description">
                          <OverflowTip>
                            {modifierGroup?.modifierGroupName}
                          </OverflowTip>
                          <span>
                            {generateModifierGroupRule(modifierGroup, {
                              modifierGroupPickUpTo:
                                textMap.modifierGroupPickUpTo,
                              pickExactly: textMap.pickExactly,
                              pickXtoY: textMap.pickXtoY,
                            })}
                          </span>
                        </div>
                        <div>
                          <OverflowTip>
                            {modifierGroup?.modifiers
                              .map(modifier => modifier?.modifierName)
                              ?.join(", ")}
                          </OverflowTip>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </div>
      <div css={drawerFooter}>
        <Button
          type="primary"
          block
          disabled={!changeIsMade}
          data-testid="btn-submit"
          onClick={handleSubmitLinkMg}
        >
          {textMap.linkBtn.replace(/<x>/gi, `${tmpLinkedModifierGroup.length}`)}
        </Button>
      </div>
    </Drawer>
  )
}
